import { BEATS, type Beat } from '../content/beats';
import { LEVELS, levelOrder, type LevelId } from '../content/sequence';
import { missionOf } from '../content/missions';
import { bandMastered, duePatterns, type MasteryMap } from './mastery';

// Builds each session's beat list and the first-run placement, per docs/03:
// mostly due/weak review + a little new, kept winnable (~80–90%), ending on a high.

const SESSION_LEN = 8;
const NEW_PER_SESSION = 2;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function beatsForPattern(patternId: string, band: LevelId): Beat[] {
  const max = levelOrder(band);
  return BEATS.filter((bt) => bt.scramblePattern === patternId && levelOrder(bt.level) <= max);
}

/**
 * A session of ~8 beats: due/weak SEEN patterns first (review, high success), plus a
 * couple of NEW patterns. Falls back to any in-band beats if due ones run short.
 */
export function buildSession(map: MasteryMap, band: LevelId, session: number): Beat[] {
  const due = duePatterns(map, band, session); // weakest first
  const seen = due.filter((id) => map[id]);
  const fresh = due.filter((id) => !map[id]);
  const order = seen.length >= NEW_PER_SESSION ? [...seen, ...fresh.slice(0, NEW_PER_SESSION)] : [...seen, ...fresh];

  const chosen: Beat[] = [];
  const used = new Set<string>();
  let guard = 0;
  while (chosen.length < SESSION_LEN && order.length > 0 && guard < order.length * 4) {
    const patternId = order[guard % order.length];
    const candidates = beatsForPattern(patternId, band).filter((bt) => !used.has(bt.id));
    if (candidates.length) {
      const beat = candidates[Math.floor(Math.random() * candidates.length)];
      chosen.push(beat);
      used.add(beat.id);
    }
    guard++;
  }

  // Fill if short: prefer beats from missions already in play (so a session tends to
  // finish one story), then any other in-band beats. Selection stays SR-led; this only
  // breaks ties toward narrative cohesion.
  if (chosen.length < SESSION_LEN) {
    const max = levelOrder(band);
    const inBandUnused = BEATS.filter((bt) => levelOrder(bt.level) <= max && !used.has(bt.id));
    const active = new Set(chosen.map((bt) => missionOf(bt.id)?.id).filter(Boolean) as string[]);
    const sameMission = shuffle(inBandUnused.filter((bt) => active.has(missionOf(bt.id)?.id ?? '')));
    const others = shuffle(inBandUnused.filter((bt) => !active.has(missionOf(bt.id)?.id ?? '')));
    for (const beat of [...sameMission, ...others]) {
      if (chosen.length >= SESSION_LEN) break;
      chosen.push(beat);
      used.add(beat.id);
    }
  }
  // Present same-mission beats contiguously (order = first appearance) so the session
  // reads as a story. Pure reordering — does not change which beats were selected.
  return groupByMission(chosen);
}

/** Group beats by mission, contiguously, preserving first-appearance order. */
function groupByMission(beats: Beat[]): Beat[] {
  const order: string[] = [];
  const groups = new Map<string, Beat[]>();
  for (const bt of beats) {
    const key = missionOf(bt.id)?.id ?? `solo:${bt.id}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(bt);
  }
  return order.flatMap((k) => groups.get(k)!);
}

// ---- Placement (first run) ----

const seededLevels = (): LevelId[] => LEVELS.filter((l) => !l.stub).sort((a, b) => a.order - b.order).map((l) => l.id);

/** Advance to the next level once the current band is fully mastered (adaptive climb). */
export function nextBand(map: MasteryMap, band: LevelId): LevelId {
  if (!bandMastered(map, band)) return band;
  const levels = seededLevels();
  const idx = levels.indexOf(band);
  return idx >= 0 && idx < levels.length - 1 ? levels[idx + 1] : band;
}

/** A short climbing warm-up: one representative beat per seeded level, easiest first.
 *  Prefer a beat whose broken word is NOT the last word — placement is his first taste,
 *  and we never want it to teach "the wrong word is always at the end" (play-test feedback). */
export function buildPlacement(): Beat[] {
  return seededLevels()
    .map((lvl) => {
      const inLevel = BEATS.filter((bt) => bt.level === lvl);
      return inLevel.find((bt) => bt.brokenIndex < bt.words.length - 1) ?? inLevel[0];
    })
    .filter((bt): bt is Beat => Boolean(bt));
}

/** Infer the starting band: the highest level passed before the first stumble. */
export function inferBand(results: { level: LevelId; passed: boolean }[]): LevelId {
  const levels = seededLevels();
  let band: LevelId = levels[0];
  for (const lvl of levels) {
    const r = results.find((x) => x.level === lvl);
    if (r?.passed) band = lvl;
    else break; // stop at the first level he stumbles on — start at the last he aced
  }
  return band;
}
